/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

/**
 * @module bulb-caption/bulbcaptionediting
 */

import { Plugin } from 'ckeditor5';
import type { ViewDowncastWriter, ModelWriter } from 'ckeditor5';

import { forEach } from 'lodash-es';
/* eslint-disable import/no-cycle */
import {
  captionElementCreator,
  getCaptionFromMedia,
  isAllowedBlock,
  isAllowedElement,
  matchMediaCaption,
} from './utils';
import { blocksWithCaptions } from '../../editorconfig.constant';
/* eslint-enable import/no-cycle */

/**
 * The bulb caption plugin. It will add captions to all of the allowed blocks. Remember to add any allowed blocks
 * also into the .scss file so that the styling persist as well.
 *
 * @extends Plugin
 */
export default class BulbCaptionEditing extends Plugin {

  lastSelectedCaption: any;

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbCaptionEditing' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const { view } = editor.editing;
    const { schema } = editor.model;
    const { data } = editor;
    const { editing } = editor;
    const { t } = editor;

    // Schema configuration.
    schema.register('caption', {
      allowIn: blocksWithCaptions,
      allowContentOf: '$block',
      isLimit: true,
    });

    // Add caption element to each image inserted without it.
    editor.model.document.registerPostFixer((writer) => this.insertMissingModelCaptionElement(writer));

    // View to model converter for the data pipeline.
    editor.conversion.for('upcast').elementToElement({
      view: matchMediaCaption,
      model: 'caption',
    });

    // Model to view converter for the data pipeline.
    const createCaptionForData = (writer) => writer.createContainerElement('figcaption');
    data.downcastDispatcher.on('insert:caption', this.captionModelToView(createCaptionForData, false));

    // Model to view converter for the editing pipeline.
    const createCaptionForEditing = captionElementCreator(view, t);
    editing.downcastDispatcher.on('insert:caption', this.captionModelToView(createCaptionForEditing));

    // Always show caption in view when something is inserted in model.
    editing.downcastDispatcher.on(
      'insert',
      this.fixCaptionVisibility((data) => data.item),
      { priority: 'high' },
    );

    // Hide caption when everything is removed from it.
    editing.downcastDispatcher.on(
      'remove',
      this.fixCaptionVisibility((data) => data.position.parent),
      { priority: 'high' },
    );

    // Update caption visibility on view in post fixer.
    view.document.registerPostFixer((writer) => this.updateCaptionVisibility(writer));
  }

  /**
   * Updates the view before each rendering, making sure that empty captions (so unnecessary ones) are hidden
   * and then visible when the element is selected.
   *
   * @private
   * @param {module:engine/view/downcastwriter~ViewDowncastWriter} viewWriter
   * @returns {Boolean} Returns `true` when the view is updated.
   */
  private updateCaptionVisibility(viewWriter: ViewDowncastWriter): boolean {
    const { mapper } = this.editor.editing;
    const lastCaption = this.lastSelectedCaption;
    let viewCaption;

    // If whole image is selected.
    const modelSelection = this.editor.model.document.selection;
    const selectedElement = modelSelection.getSelectedElement();

    if (selectedElement && isAllowedBlock(null, selectedElement)) {
      const modelCaption = getCaptionFromMedia(selectedElement);
      viewCaption = mapper.toViewElement(modelCaption);
    }

    // If selection is placed inside caption.
    const position = modelSelection.getFirstPosition();
    const modelCaption = this.getParentCaption(position.parent);

    if (modelCaption) {
      viewCaption = mapper.toViewElement(modelCaption);
    }

    // Is currently any caption selected?
    if (viewCaption) {
      // Was any caption selected before?
      if (lastCaption) {
        // Same caption as before?
        if (lastCaption === viewCaption) {
          return this.showCaption(viewCaption, viewWriter);
        }
        this.hideCaptionIfEmpty(lastCaption, viewWriter);
        this.lastSelectedCaption = viewCaption;

        return this.showCaption(viewCaption, viewWriter);
      }
      this.lastSelectedCaption = viewCaption;
      return this.showCaption(viewCaption, viewWriter);
    }
    // Was any caption selected before?
    if (lastCaption) {
      const viewModified = this.hideCaptionIfEmpty(lastCaption, viewWriter);
      this.lastSelectedCaption = null;

      return viewModified;
    }
    return false;
  }

  /**
   * Returns a converter that fixes caption visibility during the model-to-view conversion.
   * Checks if the changed node is placed inside the caption element and fixes its visibility in the view.
   *
   * @private
   * @param {Function} nodeFinder
   * @returns {Function}
   */
  private fixCaptionVisibility(nodeFinder) {
    return (evt, data, conversionApi) => {
      const node = nodeFinder(data);
      const modelCaption = this.getParentCaption(node);
      const { mapper } = this.editor.editing;
      const viewWriter = conversionApi.writer;

      if (modelCaption) {
        const viewCaption = mapper.toViewElement(modelCaption);

        if (viewCaption) {
          if (modelCaption.childCount) {
            viewWriter.removeClass('ck-hidden', viewCaption);
          } else {
            viewWriter.addClass('ck-hidden', viewCaption);
          }
        }
      }
    };
  }

  /**
   * Checks whether the data inserted to the model document has an element that has no caption inside of it.
   * If there is none, it adds it to element.
   *
   * @private
   * @param {module:engine/model/writer~ModelWriter} writer The writer to make changes with.
   * @returns {Boolean} `true` if any change was applied, `false` otherwise.
   */
  private insertMissingModelCaptionElement(writer: ModelWriter): boolean {
    const { model } = this.editor;
    const changes = model.document.differ.getChanges();

    const elementsWithoutCaptions = [];

    forEach(changes, (entry) => {
      if (entry.type === 'insert' && entry.name !== '$text') {
        const item = entry.position.nodeAfter;

        if (isAllowedBlock(null, item) && !getCaptionFromMedia(item)) {
          elementsWithoutCaptions.push(item);
        }

        // Check elements with children for nested images.
        if (!isAllowedBlock(null, item) && item.is('element') && item.childCount) {
          // eslint-disable-next-line no-restricted-syntax
          for (const nestedItem of model.createRangeIn(item).getItems()) {
            if (isAllowedBlock(null, nestedItem) && !getCaptionFromMedia(nestedItem)) {
              elementsWithoutCaptions.push(nestedItem);
            }
          }
        }
      }
    });

    forEach(elementsWithoutCaptions, (element) => {
      writer.appendElement('caption', element);
    });

    return !!elementsWithoutCaptions.length;
  }

  /**
   * Creates a converter that converts element caption model element to view element.
   *
   * @private
   * @param {Function} elementCreator
   * @param {Boolean} [hide=true] When set to `false` view element will not be inserted when it's empty.
   * @returns {Function}
   */
  private captionModelToView(elementCreator, hide = true) {
    return (evt, data, conversionApi) => {
      if (data.item.parent.name !== 'table' && !conversionApi.consumable.consume(data.item, 'insert')) {
        return;
      }
      const captionElement = data.item;
      // Return if element shouldn't be present when empty.
      if (!captionElement.childCount && !hide) {
        return;
      }

      if (isAllowedElement(captionElement.parent)) {
        const viewElement = conversionApi.mapper.toViewElement(data.range.start.parent);
        const viewCaption = elementCreator(conversionApi.writer);
        const viewWriter = conversionApi.writer;

        // Hide if empty.
        if (!captionElement.childCount) {
          viewWriter.addClass('ck-hidden', viewCaption);
        }

        this.insertViewCaptionAndBind(viewCaption, data.item, viewElement, conversionApi);
      }
    };
  }

  /**
   *  Inserts `viewCaption` at the end of `viewElement` and binds it to `modelCaption`.
   *
   * @private
   * @param {module:engine/view/containerelement~ContainerElement} viewCaption
   * @param {module:engine/model/element~ModelElement} modelCaption
   * @param {module:engine/view/containerelement~ContainerElement} viewElement
   * @param {module:engine/conversion/downcastdispatcher~DowncastConversionApi} conversionApi
   */
  // TODO CG: Add missing types
  private insertViewCaptionAndBind(viewCaption, modelCaption, viewElement, conversionApi) {
    const viewPosition = conversionApi.writer.createPositionAt(viewElement, 'end');

    conversionApi.writer.insert(viewPosition, viewCaption);
    conversionApi.mapper.bindElements(modelCaption, viewCaption);
  }

  /**
   * Checks if the provided node or one of its ancestors is a caption element, and returns it.
   *
   * @private
   * @param {module:engine/model/node~Node} node
   * @returns {module:engine/model/element~ModelElement|null}
   */
  private getParentCaption(node) {
    const ancestors = node.getAncestors({ includeSelf: true });
    const caption = ancestors.find((ancestor) => ancestor.name === 'caption');

    if (caption && caption.parent && blocksWithCaptions.includes(caption.parent.name)) {
      return caption;
    }

    return null;
  }

  /**
   * Hides a given caption in the view if it is empty.
   *
   * @private
   * @param {module:engine/view/containerelement~ContainerElement} caption
   * @param {module:engine/view/downcastwriter~ViewDowncastWriter} viewWriter
   * @returns {Boolean} Returns `true` if the view was modified.
   */
  private hideCaptionIfEmpty(caption, viewWriter: ViewDowncastWriter): boolean {
    if (!caption.childCount && !caption.hasClass('ck-hidden')) {
      viewWriter.addClass('ck-hidden', caption);
      return true;
    }
    return false;
  }

  /**
   * Shows the caption.
   *
   * @private
   * @param {module:engine/view/containerelement~ContainerElement} caption
   * @param {module:engine/view/downcastwriter~ViewDowncastWriter} viewWriter
   * @returns {Boolean} Returns `true` if the view was modified.
   */
  private showCaption(caption, viewWriter: ViewDowncastWriter): boolean {
    if (caption.hasClass('ck-hidden')) {
      viewWriter.removeClass('ck-hidden', caption);
      return true;
    }
    return false;
  }
}
